import { z } from 'zod'
import { GroceryId, HouseholdId, UserId } from './ids.ts'
import { Mode, OwnerId } from './mode.ts'

// Free text, so a quantity rides along in the title ("2x milk").
export const GroceryTitle = z.string().trim().min(1).max(200)
export type GroceryTitle = z.infer<typeof GroceryTitle>

export const GroceryItem = z.object({
	id: GroceryId,
	householdId: HouseholdId,
	ownerId: OwnerId,
	title: GroceryTitle,
	ticked: z.boolean(),
	createdAt: z.iso.datetime(),
	createdBy: UserId,
	tickedAt: z.iso.datetime().nullable(),
	tickedBy: UserId.nullable(),
})
export type GroceryItem = z.infer<typeof GroceryItem>

export const GroceryListResponse = z.object({
	items: z.array(GroceryItem),
})
export type GroceryListResponse = z.infer<typeof GroceryListResponse>

export const GroceryCreateBody = z.object({
	mode: Mode,
	title: GroceryTitle,
})
export type GroceryCreateBody = z.infer<typeof GroceryCreateBody>

export const GroceryCreateResponse = z.object({ item: GroceryItem })
export type GroceryCreateResponse = z.infer<typeof GroceryCreateResponse>

// Two titles name the same item when they match ignoring case and padding.
export const groceryTitleKey = (title: string): string =>
	title.trim().toLowerCase()

// Several items at once, e.g. a recipe's ingredients. As when adding one by
// hand, a title already on the list is skipped, or unticked if it's ticked.
export const GroceryBatchCreateBody = z.object({
	mode: Mode,
	titles: z.array(GroceryTitle).min(1).max(100),
})
export type GroceryBatchCreateBody = z.infer<typeof GroceryBatchCreateBody>

// `items` holds the new and unticked items; `alreadyListed` counts the
// titles skipped.
export const GroceryBatchCreateResponse = z.object({
	items: z.array(GroceryItem),
	alreadyListed: z.number().int().min(0),
})
export type GroceryBatchCreateResponse = z.infer<
	typeof GroceryBatchCreateResponse
>

// `ticked` is absolute rather than a toggle, so a tick queued offline and
// replayed later can't flip an item someone else has since ticked.
export const GroceryUpdateBody = z
	.object({
		title: GroceryTitle.optional(),
		ticked: z.boolean().optional(),
	})
	.refine((b) => b.title !== undefined || b.ticked !== undefined, {
		message: 'At least one of title or ticked must be provided',
	})
export type GroceryUpdateBody = z.infer<typeof GroceryUpdateBody>

export const GroceryUpdateResponse = z.object({ item: GroceryItem })
export type GroceryUpdateResponse = z.infer<typeof GroceryUpdateResponse>

export const GroceryClearTickedBody = z.object({ mode: Mode })
export type GroceryClearTickedBody = z.infer<typeof GroceryClearTickedBody>

export const GroceryClearTickedResponse = z.object({
	deletedCount: z.number().int().min(0),
})
export type GroceryClearTickedResponse = z.infer<
	typeof GroceryClearTickedResponse
>
